import React, { useEffect, useState } from 'react';

// components and helpers
import { useDispatch, useSelector } from 'react-redux';
import { statusTitle } from './helpers/StatusTitle';
import { showNotification } from '../../../Notifications/NotificationActions';
import utils from '../../../../utils/utils';

// mui
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    Divider,
} from '@mui/material';
import ErrorsTable from '../Errors/ErrorsTable';

const ImportProgressPreview: React.FC = () => {
    const [openErrors, setOpenErrors] = useState(false);
    const dispatch = useDispatch();
    const [fileName, setFileName] = useState<string>('');
    const fileImportStatus = useSelector(
        (state: any) => state.workspace.initiatedFileImport
    );

    const handleClickOpen = () => {
        setOpenErrors(true);
    };

    const handleClose = () => {
        setOpenErrors(false);
    };

    const handleCopyTx = (id) => {
        navigator.clipboard.writeText(id);
        dispatch(
            showNotification(
                'Transaction ID successfully copied to clipboard.',
                'success'
            )
        );
    };

    useEffect(() => {
        setFileName(fileImportStatus?.fileName);
    }, [fileImportStatus, fileName, dispatch]);

    const createList = (fieldset: string[]) => {
        const dates = ['createdAt', 'lastUpdated', 'added'];
        const list = [];

        // eslint-disable-next-line array-callback-return
        fieldset.map((key: string) => {
            let primary = key;
            let secondary = fileImportStatus[key];

            if (key === 'completedItems') {
                primary = 'Processed Items';
            } else if (key === 'erroredItems') {
                primary = 'Items Not Processed';
            } else if (key === 'warningItems') {
                primary = 'Warnings Identified';
            }
            if (dates.includes(key)) {
                secondary = utils.formatDate(fileImportStatus[key], 'L LTS');
            }
            if (key === 'size') {
                secondary = utils.toUserFriendlySize(fileImportStatus[key]);
            }
            if (key === 'transactionId' && !fileImportStatus[key]) {
                secondary = 'Pending';
            } else if (key === 'transactionId') {
                secondary = (
                    <Typography
                        variant={'body2'}
                        sx={{
                            color: '#999ba8',
                            textDecoration: 'underline',
                            textDecorationStyle: 'dashed',
                            '&:hover': { cursor: 'copy' },
                        }}
                        onClick={() => handleCopyTx(fileImportStatus[key])}
                    >
                        {fileImportStatus[key]}
                    </Typography>
                );
            }
            list.push(
                <ListItem
                    sx={{ width: 'auto' }}
                    key={`progress-preview-${primary}`}
                >
                    <ListItemText
                        sx={{ overflowWrap: 'anywhere' }}
                        primary={
                            primary === 'transactionId'
                                ? 'Transaction ID'
                                : utils.toTitleText(primary)
                        }
                        secondary={secondary}
                    />
                </ListItem>
            );
        });
        return list;
    };

    return (
        <Grid container>
            <Grid item xs={12}>
                <Box sx={{ width: '100%', p: 2 }}>
                    <Grid container justifyContent={'space-between'}>
                        <Grid item>
                            <Typography variant={'h6'}>
                                Imported File Status and Details
                            </Typography>
                            {fileImportStatus &&
                                fileImportStatus.status &&
                                fileImportStatus.status !== 'SUCCESS' &&
                                fileImportStatus.status !== 'ERROR' &&
                                fileImportStatus.status !== 'DRAFT' && (
                                    <Typography
                                        variant={'body2'}
                                        color={'secondary'}
                                        style={{ fontWeight: 'bold' }}
                                    >
                                        We are working on your data import. Feel
                                        free to continue use of the application
                                        - you may check the status of your
                                        import in the file list.
                                    </Typography>
                                )}
                        </Grid>
                        <Grid item>
                            {statusTitle(
                                fileImportStatus && fileImportStatus.status,
                                'dialog'
                            )}
                        </Grid>
                    </Grid>
                    <Divider />
                    {!fileImportStatus ? (
                        <Grid
                            container
                            justifyContent={'center'}
                            sx={{ pt: 3 }}
                        >
                            <Grid item>
                                <CircularProgress />
                            </Grid>
                        </Grid>
                    ) : fileImportStatus ? (
                        <Grid container spacing={2}>
                            <Grid item xs={4}>
                                <List>
                                    {createList([
                                        'fileName',
                                        'uploadType',
                                        'size',
                                        'transactionId',
                                    ])}
                                </List>
                            </Grid>
                            <Grid item xs={4}>
                                <List>
                                    {createList([
                                        'createdBy',
                                        'createdAt',
                                        'lastUpdated',
                                        'updatedBy',
                                    ])}
                                </List>
                            </Grid>
                            <Grid item xs={4}>
                                <List>
                                    {createList([
                                        'status',
                                        'totalItems',
                                        'completedItems',
                                        'erroredItems',
                                        'warningItems',
                                    ])}
                                </List>
                                <Grid item>
                                    {fileImportStatus.hasError && (
                                        <List onClick={handleClickOpen}>
                                            <Button
                                                variant={'outlined'}
                                                color={'error'}
                                                style={{
                                                    textTransform: 'none',
                                                }}
                                            >
                                                View Errors/Warnings Details
                                            </Button>
                                        </List>
                                    )}
                                </Grid>
                            </Grid>
                        </Grid>
                    ) : (
                        <Typography>
                            There was an issue fetching file details. Please
                            check back again.
                        </Typography>
                    )}
                </Box>
            </Grid>
            <Dialog
                open={openErrors}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth
                maxWidth={'lg'}
            >
                <ErrorsTable fileName={fileName} />
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default ImportProgressPreview;
